import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, LANGUAGE_STORAGE_KEY, type LanguageCode } from "./config";
import uiEn from "./ui/en.json";
import uiRu from "./ui/ru.json";
import docsEn from "../content/model-docs/en.json";
import docsRu from "../content/model-docs/ru.json";

type Dictionary = Record<string, string>;

const UI: Record<LanguageCode, Dictionary> = {
  en: uiEn as Dictionary,
  ru: uiRu as Dictionary,
};

/* ------------------------------------------------------- словари моделей */

export interface FieldDoc {
  label?: string;
  description?: string;
  /** Человекочитаемые названия значений enum: { "sec_5": "5 s" } */
  options?: Record<string, string>;
  /** Значения, реально разрешённые в UI — уже, чем список в nodeDefinition. */
  allowedValues?: string[];
}

export interface ModelDoc {
  name?: string;
  /** Короткая строка из витрины: «Create video from image». */
  tagline?: string;
  description?: string;
  inputs?: Record<string, FieldDoc>;
  params?: Record<string, FieldDoc>;
  outputs?: Record<string, FieldDoc>;
}

type DocsBundle = Record<string, ModelDoc>;

/**
 * Словари переводов и ручных правок, от старшего к младшему:
 *   1. словарь выбранного языка (model-docs/<lang>.json)
 *   2. словарь английского      (model-docs/en.json)
 *
 * Английские подписи и описания приходят с бэкенда портала уже в FieldSpec —
 * он тянет их из `airtable_ml_data` основного бэка. Эти словари нужны для
 * переводов и точечных правок поверх.
 */
const MANUAL_DOCS: Record<LanguageCode, DocsBundle> = {
  en: docsEn as unknown as DocsBundle,
  ru: docsRu as unknown as DocsBundle,
};

const EMPTY_FIELD: FieldDoc = {};

/* --------------------------------------------------------------- контекст */

interface I18nApi {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  /** Строка интерфейса. Плейсхолдеры вида {name} подставляются из vars. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  modelDoc: (idGlobal: string) => ModelDoc;
  fieldDoc: (idGlobal: string, kind: "input" | "param" | "output", name: string) => FieldDoc;
}

const I18nContext = createContext<I18nApi | null>(null);

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}

function readStoredLanguage(): LanguageCode {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored && stored in UI ? (stored as LanguageCode) : DEFAULT_LANGUAGE;
}

const BUCKETS = { input: "inputs", param: "params", output: "outputs" } as const;

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(readStoredLanguage);

  const setLanguage = useCallback((next: LanguageCode) => {
    setLanguageState(next);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  }, []);

  const t = useCallback<I18nApi["t"]>(
    (key, vars) => {
      const value = UI[language][key] ?? UI[FALLBACK_LANGUAGE][key] ?? key;
      return interpolate(value, vars);
    },
    [language],
  );

  /** Слои словарей для модели, от старшего к младшему. */
  const layersFor = useCallback(
    (idGlobal: string): ModelDoc[] =>
      [
        MANUAL_DOCS[language]?.[idGlobal],
        MANUAL_DOCS[FALLBACK_LANGUAGE]?.[idGlobal],
      ].filter(Boolean) as ModelDoc[],
    [language],
  );

  const modelDoc = useCallback<I18nApi["modelDoc"]>(
    (idGlobal) => {
      const layers = layersFor(idGlobal);
      const pick = <K extends keyof ModelDoc>(key: K): ModelDoc[K] =>
        layers.find((layer) => layer[key] !== undefined)?.[key];
      return {
        name: pick("name"),
        tagline: pick("tagline"),
        description: pick("description"),
        inputs: pick("inputs"),
        params: pick("params"),
        outputs: pick("outputs"),
      };
    },
    [layersFor],
  );

  const fieldDoc = useCallback<I18nApi["fieldDoc"]>(
    (idGlobal, kind, name) => {
      const bucket = BUCKETS[kind];
      const fields = layersFor(idGlobal)
        .map((layer) => layer[bucket]?.[name])
        .filter(Boolean) as FieldDoc[];
      if (fields.length === 0) return EMPTY_FIELD;

      // options сливаются снизу вверх: старший слой перекрывает младший поштучно
      const options = fields
        .slice()
        .reverse()
        .reduce<Record<string, string>>((acc, field) => ({ ...acc, ...(field.options ?? {}) }), {});

      return {
        label: fields.find((field) => field.label)?.label,
        description: fields.find((field) => field.description)?.description,
        allowedValues: fields.find((field) => field.allowedValues)?.allowedValues,
        options: Object.keys(options).length ? options : undefined,
      };
    },
    [layersFor],
  );

  const api = useMemo(
    () => ({ language, setLanguage, t, modelDoc, fieldDoc }),
    [language, setLanguage, t, modelDoc, fieldDoc],
  );

  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside <I18nProvider>");
  return context;
}
